import React, {Component} from "react"
import {Row} from "react-bootstrap"

import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import {H6, Body} from "@irati/ui/src/Typography"
import Card from "@irati/ui/src/Layout/Card"
import Flex from "@irati/ui/src/Flex"
import adminReportingService from "@irati/adminReporting/services/adminReporting"
import authentication from "@irati/auth"
import teamsService from "@irati/identity/services/teams"
import {Select} from "@irati/ui/src/FormItem"
import Loading from "@irati/ui/src/Loading"
import {OrganisationalSummaryCategory, OrganisationalSummaryTrendDuration, PaymentCurrencyType} from "@irati/types"
import theme from "@irati/ui/src/global/theme"
import adminTracking, {ViewType, Filter, Product} from "@irati-tracking/admin/admin"

import Region from "../Region"
import {Header} from "../../OrganisationalSummary/styled"
import {EmptyData} from "../EmptyData"
import Categories from "./Categories"
import BarChart from "./BarChart"
import GraphFilters from "./GraphFilters"
import buildTrendRates from "./buildTrendRates"
import Download from "./Download"
import {TrendTypes} from "./enums"

const ALL_TEAMS = "ALL"

interface Option {
  value: string | OrganisationalSummaryCategory | OrganisationalSummaryTrendDuration
  label: string
}

interface Filters {
  businessUnitIds?: string[]
  businessUnitId: string
  teamId?: string
  currency?: string
}

interface Summary {
  title: string
  description: string
  value: number
  isTrendRate?: boolean
}

interface Props extends InjectIntl {
  source: string
}

interface State {
  load: Promise<any> | null
  buttonLoad: Promise<any> | null
  summary: Summary[]
  teamOptions: Option[]
  timeRange: OrganisationalSummaryTrendDuration
  timeRangeOptions: Option[]
  filters: Filters
  category: OrganisationalSummaryCategory
  showNoData: boolean
  selectedTrendRate: string
  selectedTrendRateLabel: string
}

class TrendRates extends Component<Props, State> {
  state: State = {
    load: null,
    buttonLoad: null,
    summary: [],
    teamOptions: [],
    timeRange: OrganisationalSummaryTrendDuration.SummaryTrendQuarterly,
    timeRangeOptions: [],
    filters: {
      businessUnitIds: [],
      businessUnitId: ALL_TEAMS,
      teamId: ALL_TEAMS,
      currency: PaymentCurrencyType.Gbp
    },
    category: OrganisationalSummaryCategory.Main,
    showNoData: false,
    selectedTrendRate: TrendTypes.engagementRate,
    selectedTrendRateLabel: "admin.reporting.orgSummary.main.engagementRate.title"
  }

  componentDidMount() {
    const {formatMessage} = this.props

    const load = adminReportingService
      .getOrganisationalSummary({
        businessId: authentication.business().uuid,
        trendRateDuration: OrganisationalSummaryTrendDuration.SummaryTrendQuarterly
      })
      .then(async (res) => {
        const summary = buildTrendRates(
          res,
          OrganisationalSummaryCategory.Main,
          TrendTypes.engagementRate,
          PaymentCurrencyType.Gbp,
          OrganisationalSummaryTrendDuration.SummaryTrendQuarterly
        )
        if (summary?.length) {
          this.setState({res, summary})
        } else {
          this.setState({showNoData: true})
        }

        const teams = await teamsService.list(authentication.business().uuid)
        const baseTeamOptions = teams.map((team) => ({label: team.teamName, value: team.uuid}))
        const teamOptions = [{label: formatMessage({id: "admin.reporting.popularPerks.allTeams"}), value: ALL_TEAMS}, ...baseTeamOptions]
        const timeRangeOptions = [
          {
            label: formatMessage({id: "admin.reporting.trendReports.time.monthly"}),
            value: OrganisationalSummaryTrendDuration.SummaryTrendMonthly
          },
          {
            label: formatMessage({id: "admin.reporting.trendReports.time.quarterly"}),
            value: OrganisationalSummaryTrendDuration.SummaryTrendQuarterly
          }
        ]
        this.setState({teamOptions, timeRangeOptions, timeRange: OrganisationalSummaryTrendDuration.SummaryTrendQuarterly})
      })
      .catch(() => this.setState({showNoData: true}))
    this.setState({load, buttonLoad: load})
  }

  buildTeams = () => {
    const {filters} = this.state
    if (filters.businessUnitId !== ALL_TEAMS) {
      return [filters.businessUnitId]
    }
    if (filters.teamId !== ALL_TEAMS) {
      return [filters.teamId]
    }
  }

  getOrganisationSummary = () => {
    const {timeRange, category, filters, selectedTrendRate} = this.state

    const variables = {
      businessId: authentication.business().uuid,
      category,
      trendRateDuration: timeRange,
      teams: this.buildTeams()
    }
    const load = adminReportingService
      .getOrganisationalSummary(variables)
      .then(async (res) => {
        this.setState({res, summary: buildTrendRates(res, category, selectedTrendRate, filters.currency, timeRange)})
      })
      .catch(() => this.setState({showNoData: true}))
    this.setState({load, showNoData: false})
  }

  handleProductEvent = (category: string): Product | undefined => {
    switch (category) {
      case OrganisationalSummaryCategory.Perks:
        return Product.perks
      case OrganisationalSummaryCategory.FlexiPerks:
        return Product.flexipoints
      case OrganisationalSummaryCategory.Celebrations:
        return Product.celebration
      case OrganisationalSummaryCategory.Wellness:
        return Product.wellness
      default:
        return undefined
    }
  }

  handleTrackEvent = (filter: Filter | undefined, viewType: ViewType, category: string, metric?: string) => {
    adminTracking.reporting({
      source: this.props.source,
      type: "overall_trendReports",
      viewType,
      filter,
      product: this.handleProductEvent(category),
      metric
    })
  }

  handleUpdateBusinessUnit = (businessUnitId: string) => {
    this.handleTrackEvent(Filter.country, ViewType.filtered, this.state.category)
    this.setState(
      (prevState: State) => ({
        filters: {...prevState.filters, businessUnitId},
        selectedTrendRate: this.getTrendType(businessUnitId !== ALL_TEAMS, prevState.category)
      }),
      this.getOrganisationSummary
    )
  }

  handleUpdateTeam = (teamId: string) => {
    this.handleTrackEvent(Filter.team, ViewType.filtered, this.state.category)
    this.setState(
      (prevState: State) => ({
        filters: {...prevState.filters, teamId},
        selectedTrendRate: this.getTrendType(teamId !== ALL_TEAMS, prevState.category)
      }),
      this.getOrganisationSummary
    )
  }

  getTrendType = (isTeamSelected: boolean, category: OrganisationalSummaryCategory) => {
    if (isTeamSelected) {
      if (category === OrganisationalSummaryCategory.Main) {
        return TrendTypes.usersLoggedIn
      }
      if (category === OrganisationalSummaryCategory.Perks || category === OrganisationalSummaryCategory.FlexiPerks) {
        return TrendTypes.usersRedeemed
      }
      if (category === OrganisationalSummaryCategory.Celebrations || category === OrganisationalSummaryCategory.Wellness) {
        return TrendTypes.usersEngaged
      }
    }
    return TrendTypes.engagementRate
  }

  handleUpdateCurrency = (currency: string) => {
    this.handleTrackEvent(Filter.currency, ViewType.filtered, this.state.category)
    this.setState((prevState: State) => ({filters: {...prevState.filters, currency}}), this.getOrganisationSummary)
  }

  handleSetTimeRangeUpdate = (timeRange: OrganisationalSummaryTrendDuration) => {
    this.handleTrackEvent(Filter.date, ViewType.filtered, this.state.category)
    this.setState({timeRange}, this.getOrganisationSummary)
  }

  handleUpdateCategory = (category: OrganisationalSummaryCategory) => {
    const {filters} = this.state
    const selectedTrendRate = this.getTrendType(filters.teamId !== ALL_TEAMS || filters.businessUnitId !== ALL_TEAMS, category)

    this.setState(
      {
        category,
        selectedTrendRate
      },
      this.getOrganisationSummary
    )

    this.handleTrackEvent(
      undefined,
      category === OrganisationalSummaryCategory.Main ? ViewType.default : ViewType.filtered,
      category,
      "engagementRate"
    )
  }

  handleTrendRateUpdate = (selectedTrendRate) => {
    const {res, category, filters, timeRange} = this.state
    this.setState({
      selectedTrendRate: selectedTrendRate.value,
      selectedTrendRateLabel: selectedTrendRate.label,
      summary: buildTrendRates(res, category, selectedTrendRate.value, filters.currency, timeRange)
    })
    this.handleTrackEvent(
      undefined,
      this.state.category === OrganisationalSummaryCategory.Main ? ViewType.default : ViewType.filtered,
      this.state.category,
      selectedTrendRate.metric
    )
  }

  handleDownloadEvent = () => {
    adminTracking.reporting({
      source: this.props.source,
      type: "overall_trendReports",
      viewType:
        this.state.category === OrganisationalSummaryCategory.Main &&
        this.state.filters.businessUnitId === ALL_TEAMS &&
        this.state.filters.teamId === ALL_TEAMS &&
        this.state.filters.currency === PaymentCurrencyType.Gbp &&
        this.state.timeRange === OrganisationalSummaryTrendDuration.SummaryTrendMonthly
          ? ViewType.default
          : ViewType.filtered,
      action: "downloadReport"
    })
  }

  render() {
    const {
      teamOptions,
      filters,
      load,
      buttonLoad,
      category,
      summary,
      selectedTrendRate,
      selectedTrendRateLabel,
      res,
      showNoData,
      timeRangeOptions,
      timeRange
    } = this.state

    return (
      <Card marginBottom={8}>
        <Flex justifyContent="space-between" marginBottom={4}>
          <Header>
            <H6 marginBottom={1}>
              <FormattedMessage id="admin.reporting.trendReports.title" />
            </H6>
            <Body small>
              <FormattedMessage id="admin.reporting.trendReports.desc" />
            </Body>
          </Header>
          <Loading load={buttonLoad}>
            <Flex alignItems="center" gap={theme.spacing.oneAndHalf} justifyContent="flex-end">
              <Region
                id="adminSection_trendRates_country"
                value={filters.businessUnitId}
                isDisabled={filters.teamId !== ALL_TEAMS}
                onChange={this.handleUpdateBusinessUnit}
              />
              <Row>
                <Select
                  id="adminSection_trendRates_teams"
                  isDisabled={filters.businessUnitId !== ALL_TEAMS}
                  options={teamOptions}
                  value={filters.teamId}
                  onChange={this.handleUpdateTeam}
                />
              </Row>
              <Row>
                <Select
                  id="adminSection_trendRates_duration"
                  name="trendRatesDuration"
                  options={timeRangeOptions}
                  value={timeRange}
                  onChange={this.handleSetTimeRangeUpdate}
                />
              </Row>
              <Row>
                <Select
                  id="adminSection_organisationalSummary_currency"
                  isClearable={false}
                  value={filters.currency}
                  options={[
                    {value: PaymentCurrencyType.Gbp, label: PaymentCurrencyType.Gbp},
                    {value: PaymentCurrencyType.Aud, label: PaymentCurrencyType.Aud}
                  ]}
                  onChange={this.handleUpdateCurrency}
                />
              </Row>
            </Flex>
          </Loading>
        </Flex>
        <Categories onChange={this.handleUpdateCategory} selectedCategory={category} />
        <Loading load={load}>
          {showNoData ? (
            <EmptyData />
          ) : (
            <Flex width="100%" gap={theme.spacing.two} marginBottom={4}>
              <Card bgColor={theme.colors.greys.grey03} padding={[4, 1.5]}>
                <GraphFilters
                  onChange={this.handleTrendRateUpdate}
                  isTeamSelected={filters.teamId !== ALL_TEAMS || filters.businessUnitId !== ALL_TEAMS}
                  selectedTrendRate={selectedTrendRate}
                  selectedCategory={category}
                />
              </Card>
              <BarChart
                data={summary}
                selectedTrendRate={selectedTrendRate}
                selectedCategory={category}
                selectedTrendRateLabel={selectedTrendRateLabel}
                currencyCode={filters.currency}
              />
            </Flex>
          )}
        </Loading>
        <Download
          summary={summary}
          res={res}
          isTeamSelected={filters.teamId !== ALL_TEAMS || filters.businessUnitId !== ALL_TEAMS}
          currencyCode={filters.currency}
          category={category}
          onCallback={this.handleDownloadEvent}
        />
      </Card>
    )
  }
}

export default injectIntl(TrendRates)
